// ITER283c — Brief-form cuisine choices + client→canonical mapping.
//
// Editable data. Any redesigner or reviewer can add/remove/relabel
// cuisines here without touching the brief-form components. Every
// client-facing chip maps to ONE canonical in
// /app/backend/config/cuisine_aliases.json (or `null` when the
// concept has no live canonical yet — that chip is captured on the
// brief unchanged and flows into the engine's unaliased-cuisine
// fallback with the report caveat).
//
// Editing rules:
//   • Keep labels client-facing (no engine terms; no slashes bundling
//     two unrelated concepts; no price words).
//   • `canonical` MUST equal an existing key under `aliases` in
//     `/app/backend/config/cuisine_aliases.json`, OR be `null` for a
//     recognised-but-unmapped concept.
//   • If you add a concept whose canonical is still in the backend
//     registry's `_candidates` block, set `canonical: null` and add
//     the concept to `UNMAPPED_CLIENT_CUISINES` below (surfaced in
//     the completion report for Lokesha's review).
//
// The engine reads `preferred_cuisines` as an ORDERED list. The UI
// writes CANONICAL names into that list when a mapping exists, and
// the raw client label otherwise — the engine's alias-registry then
// resolves the canonical exact-match automatically.
export const CUISINE_CHOICES = [
  { label: "Indo-Chinese",       canonical: "indochinese" },
  { label: "North Indian",       canonical: "north_indian" },
  { label: "Mughlai",            canonical: "north_indian" },
  { label: "Punjabi",            canonical: "north_indian" },
  { label: "Awadhi",             canonical: "north_indian" },
  { label: "Tandoor / Kebab",    canonical: "north_indian" },
  { label: "South Indian",       canonical: "south_indian" },
  { label: "Chettinad",          canonical: "south_indian" },
  { label: "Andhra",             canonical: "south_indian" },
  { label: "Kerala",             canonical: "south_indian" },
  { label: "Malabar",            canonical: "south_indian" },
  { label: "Italian",            canonical: "italian" },
  { label: "Pizza",              canonical: "italian" },
  { label: "Japanese",           canonical: "japanese" },
  { label: "Sushi",              canonical: "japanese" },
  { label: "Ramen",              canonical: "japanese" },
  { label: "Mexican",            canonical: "mexican" },
  { label: "Bakery",             canonical: "bakery" },
  { label: "Patisserie",         canonical: "bakery" },
  { label: "Cafe / Coffee",      canonical: "cafe" },
  { label: "Coastal / Seafood",  canonical: "seafood" },
  { label: "Malvani",            canonical: "seafood" },
  { label: "Konkani",            canonical: "seafood" },
  { label: "Mangalorean",        canonical: "seafood" },
  { label: "Biryani",            canonical: "biryani" },
  { label: "Hyderabadi Biryani", canonical: "biryani" },
  // Recognised concepts with NO live canonical yet — engine falls to
  // exact-match + unaliased flag → report caveat.
  { label: "Continental European", canonical: null },
  { label: "Pan-Asian",            canonical: null },
  { label: "Thai",                 canonical: null },
  { label: "Vietnamese",           canonical: null },
  { label: "Korean",               canonical: null },
  { label: "Middle Eastern",       canonical: null },
  { label: "Bengali",              canonical: null },
  { label: "Gujarati",             canonical: null },
  { label: "Marathi",              canonical: null },
  { label: "Rajasthani",           canonical: null },
  { label: "Kashmiri",             canonical: null },
  { label: "Goan",                 canonical: null },
  { label: "Parsi",                canonical: null },
];

// Chips that carry `canonical: null` — surfaced here for the review
// list Lokesha red-pens. Move a client label out of this list when
// its canonical is added to the backend registry's live `aliases`.
export const UNMAPPED_CLIENT_CUISINES = CUISINE_CHOICES
  .filter((c) => c.canonical === null)
  .map((c) => c.label);

// Resolve a client label (or free-text) to the engine's list value.
// Mapped labels resolve to their canonical; unmapped and free-text
// pass through unchanged so the backend alias registry can do its
// exact-match / unaliased-flag work.
export function resolveCuisineToEngineValue(label) {
  if (!label) return null;
  const hit = CUISINE_CHOICES.find(
    (c) => c.label.toLowerCase() === String(label).toLowerCase(),
  );
  if (hit && hit.canonical) return hit.canonical;
  return String(label).trim();
}

// ITER283c-fix — Normalise a user-typed cuisine label for de-dup:
// lowercase → trim → collapse internal whitespace, hyphens and
// underscores into a single space. So "Pan-Asian", "pan_asian",
// "PAN ASIAN" and " Pan  Asian " all resolve to the SAME normal
// form ("pan asian"). Used both for chip-lookup and for identity
// keys.
export function normalizeCuisineLabel(s) {
  if (s === null || s === undefined) return "";
  return String(s)
    .toLowerCase()
    .trim()
    .replace(/[\s_-]+/g, " ");
}

// ITER283c-fix — Given a user-typed (or engine-stored) cuisine string,
// return the CUISINE_CHOICES chip whose label OR canonical matches.
// Falls back to:
//   (a) normalised-label EXACT match ("Pan-Asian" ↔ "pan_asian" ↔
//       "PAN ASIAN" all → the Pan-Asian chip), then
//   (b) canonical EXACT match ("indochinese" → Indo-Chinese chip),
//       then
//   (c) UNIQUE-prefix match — if EXACTLY one chip's normalised label
//       starts with the input's normalised form, resolve to that
//       chip. So "conti" and "continental" both resolve to the
//       Continental European chip (unique prefix). If more than one
//       chip shares the prefix, no resolution (returns null) — the
//       caller then treats the value as new free-text.
// Returns null when no chip resolves.
export function resolveLabelToClientChip(text) {
  if (!text) return null;
  const norm = normalizeCuisineLabel(text);
  if (!norm) return null;
  // (a) Exact chip-label match on normalised form.
  const byLabel = CUISINE_CHOICES.find(
    (c) => normalizeCuisineLabel(c.label) === norm,
  );
  if (byLabel) return byLabel;
  // (b) Exact canonical match on the engine key.
  const byCanonical = CUISINE_CHOICES.find(
    (c) => c.canonical && c.canonical === norm.replace(/\s+/g, "_"),
  );
  if (byCanonical) return byCanonical;
  // (c) Unique-prefix match (input is a strict prefix of exactly one
  //     chip's normalised label). Guarded on uniqueness so a bare
  //     "in" doesn't ambiguously map to Indian OR Indo-Chinese, etc.
  const prefixed = CUISINE_CHOICES.filter(
    (c) => normalizeCuisineLabel(c.label).startsWith(norm),
  );
  if (prefixed.length === 1) return prefixed[0];
  return null;
}

// ITER283c-fix — Compute an identity key for a cuisine engine value
// so downstream code can de-duplicate. Chips resolve to their
// canonical (when mapped) or normalised label; free-text falls to a
// namespaced normalised custom identity. Two entries with the same
// key are considered the SAME cuisine even if their raw strings
// differ (case / whitespace / underscore-hyphen variance /
// canonical-vs-label). Concretely:
//   ['Pan-Asian','pan_asian']              → both 'chip:pan asian'
//   ['indochinese','Indo-Chinese']         → both 'canonical:indochinese'
//   ['continental','Continental European'] → both 'chip:continental european'
export function cuisineIdentityKey(engineValue) {
  const chip = resolveLabelToClientChip(engineValue);
  if (chip && chip.canonical) return `canonical:${chip.canonical}`;
  if (chip) return `chip:${normalizeCuisineLabel(chip.label)}`;
  return `custom:${normalizeCuisineLabel(engineValue)}`;
}
